import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, LineChart } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { Button, Card, ErrorState, Spinner, cx } from '../components/ui';

interface Indicator {
  id: string;
  label: string;
  help: string | null;
  type: string;
  unit: string | null;
  min: number | null;
  max: number | null;
  step: number | null;
  options: Array<{ value: string; label: string }> | null;
  anchors: { min: string; max: string } | null;
  required: boolean;
  program: { id: string; name: string; brand: string | null } | null;
}

interface SubmitResponse {
  messages: Array<{ message: string; severity: string }>;
}

/**
 * Registrar un indicador.
 *
 * Un indicador, un valor. La pantalla se arma leyendo su definición: la pharma
 * la configura desde la consola y acá no hay nada específico de ningún
 * tratamiento ni de ningún fármaco.
 */
export function IndicatorPage() {
  const { slug, indicatorId } = useParams<{ slug: string; indicatorId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const base = `/${slug}/app`;

  const [value, setValue] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<SubmitResponse | null>(null);

  const indicators = useQuery({
    queryKey: ['me', 'indicators'],
    queryFn: () => api.get<{ indicators: Indicator[] }>('/me/indicators'),
  });

  const indicator = indicators.data?.indicators.find((entry) => entry.id === indicatorId);

  const submit = useMutation({
    mutationFn: () => api.post<SubmitResponse>(`/me/indicators/${indicatorId}/measurements`, { value }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      setDone(result);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'No se pudo guardar. Probá de nuevo.'),
  });

  if (indicators.isLoading) return <Spinner />;
  if (indicators.isError) {
    return <ErrorState error={indicators.error} onRetry={() => indicators.refetch()} />;
  }

  if (!indicator) {
    return (
      <div className="max-w-lg mx-auto px-5 py-8">
        <Card className="p-6 text-center">
          <p className="font-semibold">Esto ya no se te pide</p>
          <p className="text-fg-muted text-[15px] mt-1">
            Tu equipo dejó de seguir este dato. Lo que registraste antes sigue en tu historial.
          </p>
          <Link to={base}>
            <Button className="mt-4">Volver al inicio</Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-lg mx-auto px-5 py-8">
        <Card className="p-6 text-center">
          <div className="size-12 rounded-full bg-success-soft text-success grid place-items-center mx-auto">
            <Check className="size-6" />
          </div>
          <p className="font-semibold text-lg mt-3">Listo, quedó registrado</p>

          {/* Solo el mensaje que la pharma escribió para el paciente. El texto
              interno de la alerta es para el equipo, no para él. */}
          {done.messages.map((message, index) => (
            <p key={index} className="text-[15px] text-fg-muted mt-2">
              {message.message}
            </p>
          ))}

          <div className="flex flex-col gap-2 mt-5">
            <Button onClick={() => navigate(base)}>Volver al inicio</Button>
            <Link to={`${base}/historial/${indicator.id}`}>
              <Button variant="outline" fullWidth>
                <LineChart className="size-4" />
                Ver cómo viene
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const filled = value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);

  return (
    <div className="max-w-lg mx-auto px-5 py-6 safe-top">
      <Link to={base} className="inline-flex items-center gap-1.5 text-sm text-fg-muted mb-4">
        <ArrowLeft className="size-4" />
        Inicio
      </Link>

      <h1 className="text-xl font-semibold tracking-tight">{indicator.label}</h1>
      {indicator.program && (
        <p className="text-sm text-fg-muted mt-0.5">
          {indicator.program.brand ?? indicator.program.name}
        </p>
      )}
      {indicator.help && <p className="text-[15px] text-fg-muted mt-2">{indicator.help}</p>}

      <Card className="p-5 mt-5">
        <IndicatorInput indicator={indicator} value={value} onChange={setValue} />
      </Card>

      {error && <p className="text-sm text-danger mt-3">{error}</p>}

      <Button
        fullWidth
        size="lg"
        className="mt-5"
        loading={submit.isPending}
        disabled={!filled}
        onClick={() => {
          setError(null);
          submit.mutate();
        }}
      >
        Guardar
      </Button>

      <Link to={`${base}/historial/${indicator.id}`} className="block mt-3">
        <Button variant="ghost" fullWidth>
          Ver cómo viene
        </Button>
      </Link>
    </div>
  );
}

/**
 * El campo, según lo que declaró la pharma.
 *
 * Cada clase de indicador se dibuja de una forma y nada más: agregar una nueva
 * es agregar un caso acá, no una pantalla nueva.
 */
function IndicatorInput({
  indicator,
  value,
  onChange,
}: {
  indicator: Indicator;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  switch (indicator.type) {
    case 'SCALE': {
      const min = indicator.min ?? 0;
      const max = indicator.max ?? 10;
      const current = typeof value === 'number' ? value : null;

      return (
        <div>
          <div className="flex flex-wrap gap-1.5 justify-center">
            {Array.from({ length: max - min + 1 }, (_, index) => min + index).map((number) => (
              <button
                key={number}
                onClick={() => onChange(number)}
                className={cx(
                  'size-11 rounded-lg font-semibold tabular-nums transition-colors',
                  current === number
                    ? 'bg-primary text-primary-fg'
                    : 'bg-surface border border-border text-fg-muted',
                )}
              >
                {number}
              </button>
            ))}
          </div>

          {/* Los anclajes son parte del instrumento, no decoración: cambian lo
              que el paciente responde. */}
          {indicator.anchors && (
            <div className="flex justify-between text-xs text-fg-muted mt-3">
              <span>{indicator.anchors.min}</span>
              <span>{indicator.anchors.max}</span>
            </div>
          )}
        </div>
      );
    }

    case 'NUMBER':
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            inputMode="decimal"
            step={indicator.step ?? 'any'}
            min={indicator.min ?? undefined}
            max={indicator.max ?? undefined}
            value={typeof value === 'number' ? value : ''}
            onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
            className="w-full h-14 px-4 text-2xl font-semibold text-center rounded-md border border-border bg-surface-raised outline-none focus:border-primary"
          />
          {indicator.unit && <span className="text-lg text-fg-muted shrink-0">{indicator.unit}</span>}
        </div>
      );

    case 'BOOLEAN':
      return (
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Sí', on: true },
            { label: 'No', on: false },
          ].map((option) => (
            <button
              key={option.label}
              onClick={() => onChange(option.on)}
              className={cx(
                'h-14 rounded-lg font-semibold transition-colors',
                value === option.on
                  ? 'bg-primary text-primary-fg'
                  : 'bg-surface border border-border text-fg-muted',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      );

    case 'CHOICE':
      return (
        <div className="space-y-2">
          {(indicator.options ?? []).map((option) => (
            <button
              key={option.value}
              onClick={() => onChange(option.value)}
              className={cx(
                'w-full text-left px-4 py-3 rounded-lg font-medium transition-colors',
                value === option.value
                  ? 'bg-primary text-primary-fg'
                  : 'bg-surface border border-border',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      );

    case 'MULTICHOICE': {
      const selected = Array.isArray(value) ? (value as string[]) : [];

      return (
        <div className="space-y-2">
          {(indicator.options ?? []).map((option) => {
            const on = selected.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() =>
                  onChange(on ? selected.filter((entry) => entry !== option.value) : [...selected, option.value])
                }
                className={cx(
                  'w-full text-left px-4 py-3 rounded-lg font-medium transition-colors flex items-center gap-3',
                  on ? 'bg-primary text-primary-fg' : 'bg-surface border border-border',
                )}
              >
                <span
                  className={cx(
                    'size-5 rounded border grid place-items-center shrink-0',
                    on ? 'border-primary-fg' : 'border-border',
                  )}
                >
                  {on && <Check className="size-3.5" />}
                </span>
                {option.label}
              </button>
            );
          })}
        </div>
      );
    }

    case 'TEXT':
      return (
        <textarea
          rows={4}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value || null)}
          className="w-full px-3 py-2.5 rounded-md border border-border bg-surface-raised outline-none focus:border-primary"
        />
      );

    case 'FILE':
      return (
        <div>
          <p className="text-sm text-fg-muted mb-2">
            Sacale una foto al resultado o subí el archivo.
          </p>
          <input
            type="url"
            placeholder="Pegá el enlace del archivo"
            value={typeof value === 'object' && value ? String((value as { url?: string }).url ?? '') : ''}
            onChange={(event) => onChange(event.target.value ? { url: event.target.value } : null)}
            className="w-full h-11 px-3 rounded-md border border-border bg-surface-raised outline-none focus:border-primary"
          />
        </div>
      );

    default:
      return <p className="text-sm text-fg-muted">No sabemos cómo pedirte este dato.</p>;
  }
}
